import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { State } from './types';
import { endpoints, baseQuery } from 'utils/api/endpoints';

export const initialState: State = {};

const slice = createSlice({
  name: 'course',
  initialState,
  reducers: {},
});

export const api = createApi({
  reducerPath: 'courseApi',
  baseQuery,
  tagTypes: ['course', 'course_categories', 'course_tags'],
  endpoints: builder => ({
    getCourseList: builder.query({
      query: () => ({
        url: endpoints.COURSES + '/list',
        method: 'GET',
      }),
      providesTags: ['course'],
    }),

    getCourseDetail: builder.query({
      query: id => ({
        url: endpoints.COURSES + '/' + id,
        method: 'GET',
      }),
    }),

    getCourseCategories: builder.query({
      query: () => ({
        url: endpoints.COURSE_CATEGORIES + '/list',
        method: 'GET',
      }),
      providesTags: ['course_categories'],
    }),

    getCourseTags: builder.query({
      query: () => ({
        url: endpoints.COURSE_TAGS + '/list',
        method: 'GET',
      }),
      providesTags: ['course_tags'],
    }),

    addCourse: builder.mutation({
      query: data => ({
        url: endpoints.COURSES,
        method: 'POST',
        body: data,
      }),
    }),

    updateCourse: builder.mutation({
      query: ({ course_id, ...data }) => ({
        url: endpoints.COURSES + '/' + course_id,
        method: 'PUT',
        body: data,
      }),
    }),

    addCourseTags: builder.mutation({
      query: data => ({
        url: endpoints.COURSE_TAGS,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['course_tags'],
    }),

    addCourseCategories: builder.mutation({
      query: data => ({
        url: endpoints.COURSE_CATEGORIES + '/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['course_categories'],
    }),

    updateCourseCategories: builder.mutation({
      query: ({ id, ...data }) => ({
        url: endpoints.COURSE_CATEGORIES + '/update/' + id,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['course_categories'],
    }),

    deleteCourseCategories: builder.mutation({
      query: id => ({
        url: endpoints.COURSE_CATEGORIES + '/delete/' + id,
        method: 'DELETE',
      }),
      invalidatesTags: ['course_categories'],
    }),

    deleteCourseTags: builder.mutation({
      query: id => ({
        url: endpoints.COURSE_TAGS + '/delete/' + id,
        method: 'DELETE',
      }),
      invalidatesTags: ['course_tags'],
    }),
  }),
});

export const { actions: CourseActions } = slice;

export const useCourseSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: CourseActions,
    useLazyGetCourseListQuery: api.useLazyGetCourseListQuery,
    useLazyGetCourseDetailQuery: api.useLazyGetCourseDetailQuery,
    useLazyGetCourseCategoriesQuery: api.useLazyGetCourseCategoriesQuery,
    useLazyGetCourseTagsQuery: api.useLazyGetCourseTagsQuery,
    useAddCourseMutation: api.useAddCourseMutation,
    useUpdateCourseMutation: api.useUpdateCourseMutation,
    useAddCourseTagsMutation: api.useAddCourseTagsMutation,
    useAddCourseCategoriesMutation: api.useAddCourseCategoriesMutation,
    useDeleteCourseCategoriesMutation: api.useDeleteCourseCategoriesMutation,
    useDeleteCourseTagsMutation: api.useDeleteCourseTagsMutation,
    useUpdateCourseCategoriesMutation: api.useUpdateCourseCategoriesMutation,
  };
};
